import { CloseCircleFilled, CloseOutlined } from "@ant-design/icons";
import { DeleteButton, DeleteButtonProps } from "@refinedev/antd";
import { useTranslate } from "@refinedev/core";
import { Button, Space, Popover } from "antd";
import { useAntTheme } from "hooks/useAntTheme/";
import { useState, useRef } from "react";
import { AxiosError } from "axios";

const ERROR_TRANSLATIONS: Array<{ suffix: string; key: string }> = [
  { suffix: "voice_profiles", key: "media.voices.index.error" },
  { suffix: "face_profiles", key: "media.faces.index.error" },
  { suffix: "contacts", key: "media.contacts.index.error" },
];

export const DeleteButtonWithErrorPopover = (props: DeleteButtonProps) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const deleteButtonRef = useRef<HTMLDivElement>(null);

  const popoverDefaultPadding = 12;

  const errorMessages = (resource: string, count: number) => {
    const basePath = resource.split("?")[0]; //clean the query params
    const match = ERROR_TRANSLATIONS.find(({ suffix }) =>
      basePath?.endsWith(suffix)
    );
    return match ? t(match.key, { count }) : null;
  };

  const handleErrorNotification = (error: unknown) => {
    const axiosError = error as AxiosError | undefined;
    if (axiosError?.response?.status !== 400) {
      return {
        type: "error" as const,
        message: axiosError?.message ?? "",
      };
    }
    const errorData = axiosError.response.data as
      | {
          detail?: Record<string, string | string[]>;
        }
      | undefined;
    const errorDetails = errorData?.detail;

    const count = Object.entries(errorDetails ?? {}).reduce(
      (sum, [key, value]) => {
        if (key !== "message" && Array.isArray(value)) {
          return sum + value.length;
        }
        return sum;
      },
      0
    );

    // Resource is an optional prop
    const message = props.resource
      ? errorMessages(props.resource, count)
      : null;

    setErrorMessage(message ?? axiosError.message);
    return false;
  };

  return (
    <Popover
      title={
        <Space>
          <CloseCircleFilled style={{ color: theme.colorError }} />
          {t("src.App.error")}
        </Space>
      }
      content={
        <>
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              setErrorMessage(null);
            }}
            style={{
              position: "absolute",
              right: popoverDefaultPadding,
              top: popoverDefaultPadding,
              color: theme.colorTextBase,
            }}
          />
          {errorMessage}
        </>
      }
      open={errorMessage !== null}
      onOpenChange={(open) => !open && setErrorMessage(null)}
      trigger="click"
    >
      <div ref={deleteButtonRef} onClick={(e) => e.stopPropagation()}>
        <DeleteButton errorNotification={handleErrorNotification} {...props}>
          {t("actions.delete")}
        </DeleteButton>
      </div>
    </Popover>
  );
};
